/**
 * Audio source manager. Owns the AudioContext and the active FeatureProvider.
 * Switching sources tears down the previous one cleanly.
 */

import { LiveAnalyser } from "./analyser";
import { AnalysisJson, PreanalyzedProvider } from "./preanalyzed";
import { FeatureProvider, SourceInfo, ZERO_FEATURES, AudioFeatures } from "./types";

class NullProvider implements FeatureProvider {
  read(): AudioFeatures {
    return ZERO_FEATURES;
  }
  stop(): void {
    /* no-op */
  }
}

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private provider: FeatureProvider = new NullProvider();
  private currentInfo: SourceInfo = { kind: "none", label: "No audio source." };
  private currentTeardown: (() => void) | null = null;

  /** Lazy-create the AudioContext (must happen after a user gesture). */
  private getCtx(): AudioContext {
    if (!this.ctx) {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctor) throw new Error("WebAudio not supported in this browser.");
      this.ctx = new Ctor();
    }
    if (this.ctx.state === "suspended") {
      void this.ctx.resume();
    }
    return this.ctx;
  }

  read(): AudioFeatures {
    return this.provider.read();
  }

  info(): SourceInfo {
    return this.currentInfo;
  }

  /**
   * Explicitly wake the AudioContext. iOS Safari leaves the context in
   * `"suspended"` state until a user gesture awaits a resume() call, so
   * UI buttons should call this in the same call stack as the click.
   */
  async resume(): Promise<void> {
    const ctx = this.getCtx();
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
  }

  private swap(provider: FeatureProvider, info: SourceInfo, teardown: () => void): void {
    try {
      this.provider.stop();
    } catch {
      /* ignore */
    }
    if (this.currentTeardown) {
      try {
        this.currentTeardown();
      } catch {
        /* ignore */
      }
    }
    this.provider = provider;
    this.currentInfo = info;
    this.currentTeardown = teardown;
  }

  // --------------------------------------------------------------------
  // Microphone
  // --------------------------------------------------------------------
  async useMicrophone(): Promise<SourceInfo> {
    return this.useInput();
  }

  /**
   * Use a specific audio input device by id (Bluetooth, USB DAC, line-in,
   * TRRS mic, etc). Without an id, falls back to the system default.
   *
   * Listing devices requires that the user has already granted mic
   * permission once — this is a browser security rule. Call `enumerateInputs`
   * after a successful capture to populate the device list.
   */
  async useInput(deviceId?: string): Promise<SourceInfo> {
    const ctx = this.getCtx();
    const audioConstraints: MediaTrackConstraints = {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    };
    if (deviceId) audioConstraints.deviceId = { exact: deviceId };
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: audioConstraints,
      video: false,
    });
    const source = ctx.createMediaStreamSource(stream);
    const analyser = new LiveAnalyser(ctx, source);
    const trackLabel = stream.getAudioTracks()[0]?.label || "Mic";
    const info: SourceInfo = {
      kind: "mic",
      label: trackLabel,
      stream,
    };
    this.swap(analyser, info, () => {
      stream.getTracks().forEach((t) => t.stop());
      try {
        source.disconnect();
      } catch {
        /* ignore */
      }
    });
    return info;
  }

  /**
   * Returns the currently-available audio input devices. Labels are only
   * populated after the user has granted mic permission at least once.
   * Returns an empty array if the platform does not support enumeration.
   */
  async enumerateInputs(): Promise<MediaDeviceInfo[]> {
    const md = navigator.mediaDevices;
    if (!md || !md.enumerateDevices) return [];
    const devices = await md.enumerateDevices();
    return devices.filter((d) => d.kind === "audioinput");
  }

  // --------------------------------------------------------------------
  // Tab / system audio (desktop Chrome/Edge)
  // --------------------------------------------------------------------
  async useTabAudio(): Promise<SourceInfo> {
    const ctx = this.getCtx();
    const md = navigator.mediaDevices as MediaDevices & {
      getDisplayMedia?: (
        constraints: MediaStreamConstraints,
      ) => Promise<MediaStream>;
    };
    if (!md.getDisplayMedia) {
      throw new Error(
        "Tab / system audio capture isn't supported in this browser. On phones, use the mic source (hold near a speaker or use a Bluetooth/wired mic) or load a local audio file.",
      );
    }
    const stream = await md.getDisplayMedia({
      audio: true,
      video: true, // required to get the audio prompt
    });
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      stream.getTracks().forEach((t) => t.stop());
      throw new Error(
        "No audio track in shared tab. Pick 'Chrome Tab' and tick 'Share tab audio'.",
      );
    }
    // Discard video — we only want the audio.
    stream.getVideoTracks().forEach((t) => {
      t.stop();
      stream.removeTrack(t);
    });
    const source = ctx.createMediaStreamSource(new MediaStream(audioTracks));
    const analyser = new LiveAnalyser(ctx, source);
    const info: SourceInfo = {
      kind: "tab",
      label: "Tab / system audio",
      stream,
    };
    this.swap(analyser, info, () => {
      stream.getTracks().forEach((t) => t.stop());
      try {
        source.disconnect();
      } catch {
        /* ignore */
      }
    });
    return info;
  }

  // --------------------------------------------------------------------
  // Local file (audio plays through speakers + feeds analyser)
  // --------------------------------------------------------------------
  useFile(file: File): SourceInfo {
    const ctx = this.getCtx();
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.src = url;
    audio.crossOrigin = "anonymous";
    audio.loop = false;
    audio.controls = false;

    const source = ctx.createMediaElementSource(audio);
    // Split: one path to speakers, one to analyser, one to a recordable stream.
    const recDest = ctx.createMediaStreamDestination();
    source.connect(ctx.destination);
    source.connect(recDest);
    const analyser = new LiveAnalyser(ctx, source);

    void audio.play().catch(() => {
      // iOS / Safari often blocks autoplay until a user gesture. The UI
      // exposes the audio element via SourceInfo.mediaElement so a tap on
      // a "play" button or the canvas can resume it later.
    });

    const info: SourceInfo = {
      kind: "file",
      label: file.name,
      mediaElement: audio,
      recordStream: recDest.stream,
    };
    this.swap(analyser, info, () => {
      audio.pause();
      audio.src = "";
      URL.revokeObjectURL(url);
      try {
        source.disconnect();
      } catch {
        /* ignore */
      }
    });
    return info;
  }

  // --------------------------------------------------------------------
  // Remote URL (direct audio link, CORS-permitting)
  // --------------------------------------------------------------------
  useRemoteUrl(url: string, label?: string, audible = true): SourceInfo {
    const ctx = this.getCtx();
    const audio = new Audio();
    audio.src = url;
    audio.crossOrigin = "anonymous";
    audio.loop = false;
    audio.controls = false;

    const source = ctx.createMediaElementSource(audio);
    const recDest = ctx.createMediaStreamDestination();
    if (audible) source.connect(ctx.destination);
    source.connect(recDest);
    const analyser = new LiveAnalyser(ctx, source);

    void audio.play().catch(() => {
      // Playback may require an additional user gesture on some platforms.
    });

    const info: SourceInfo = {
      kind: "file",
      label: label || "Remote audio",
      mediaElement: audio,
      recordStream: recDest.stream,
    };
    this.swap(analyser, info, () => {
      audio.pause();
      audio.src = "";
      try {
        source.disconnect();
      } catch {
        /* ignore */
      }
    });
    return info;
  }

  // --------------------------------------------------------------------
  // Pre-analyzed: separate audio file + JSON
  // --------------------------------------------------------------------
  usePreanalyzed(audioFile: File | null, analysis: AnalysisJson): SourceInfo {
    const ctx = this.getCtx();
    const audio = new Audio();
    let url: string | null = null;
    let recordStream: MediaStream | undefined;

    if (audioFile) {
      url = URL.createObjectURL(audioFile);
      audio.src = url;
      audio.crossOrigin = "anonymous";

      const source = ctx.createMediaElementSource(audio);
      const recDest = ctx.createMediaStreamDestination();
      source.connect(ctx.destination);
      source.connect(recDest);
      recordStream = recDest.stream;
    }

    const provider = new PreanalyzedProvider(audio, analysis);
    if (audioFile) {
      void audio.play().catch(() => {
        /* user must press play later */
      });
    }

    const info: SourceInfo = {
      kind: "preanalyzed",
      label: audioFile ? `Pre-analyzed: ${audioFile.name}` : "Pre-analyzed (no audio)",
      mediaElement: audio,
      recordStream,
    };
    this.swap(provider, info, () => {
      audio.pause();
      audio.src = "";
      if (url) URL.revokeObjectURL(url);
    });
    return info;
  }

  /** Optional: the active AudioContext, useful for the recorder. */
  contextOrNull(): AudioContext | null {
    return this.ctx;
  }

  dispose(): void {
    try {
      this.provider.stop();
    } catch {
      /* ignore */
    }
    if (this.currentTeardown) {
      try {
        this.currentTeardown();
      } catch {
        /* ignore */
      }
    }
    this.provider = new NullProvider();
    this.currentTeardown = null;
    this.currentInfo = { kind: "none", label: "No audio source." };
    const context = this.ctx;
    this.ctx = null;
    if (context && context.state !== "closed") {
      void context.close().catch(() => {
        /* ignore */
      });
    }
  }
}
